import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


export default function MailMachineAssociationDialog({
  open,
  saving = false,
  onClose,
  onSearchMachines,
  onAssociate,
}) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setItems([]);
      setSelectedMachine(null);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    const term = search.trim();

    if (
      !open
      || !onSearchMachines
      || term.length < 2
      || selectedMachine
    ) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const response = await onSearchMachines(term);

        if (requestId !== requestRef.current) return;
        setItems(response?.items || []);
      } catch {
        if (requestId === requestRef.current) setItems([]);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, onSearchMachines, search, selectedMachine]);

  if (!open) return null;

  function choose(machine) {
    setSelectedMachine(machine);
    setSearch(machine?.name || "");
    setItems([]);
  }

  return (
    <ActaModal
      open
      onClose={saving ? undefined : onClose}
      title="Associer à une machine"
    >
      <div
        className="min-w-0 space-y-4 [overflow-wrap:anywhere]"
        data-testid="mail-machine-association-dialog"
      >
        <div>
          <label
            htmlFor="mail-machine-association-search"
            className="mb-1.5 block text-xs font-black uppercase tracking-wide text-[var(--do-muted)]"
          >
            Machine
          </label>

          <div className="relative">
            <MagnifyingGlass
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />
            <Input
              id="mail-machine-association-search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedMachine(null);
              }}
              placeholder="Nom, série, modèle, fabricant, client ou site"
              className="min-h-[44px] pl-10"
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
          {loading ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Recherche…
            </div>
          ) : items.length > 0 ? (
            items.map((machine) => (
              <button
                key={machine.id}
                type="button"
                onClick={() => choose(machine)}
                className="flex min-h-[64px] w-full flex-col border-b border-[var(--do-border)] px-4 py-3 text-left last:border-b-0 hover:bg-[var(--do-bg-2)]"
              >
                <span className="text-sm font-black text-[var(--do-ink)]">
                  {machine.name || "Machine"}
                </span>
                <span className="mt-1 text-xs text-[var(--do-muted)]">
                  {[
                    machine.serialNumber,
                    machine.modelName,
                    machine.manufacturer,
                    machine.contactName,
                    machine.company,
                    machine.siteName,
                  ].filter(Boolean).join(" · ")}
                </span>
              </button>
            ))
          ) : selectedMachine ? (
            <div className="p-4">
              <div className="text-sm font-black text-[var(--do-ink)]">
                {selectedMachine.name || "Machine"}
              </div>
              <div className="mt-1 text-xs text-[var(--do-muted)]">
                {[
                  selectedMachine.serialNumber,
                  selectedMachine.modelName,
                  selectedMachine.manufacturer,
                  selectedMachine.contactName,
                  selectedMachine.company,
                  selectedMachine.siteName,
                ].filter(Boolean).join(" · ")}
              </div>
            </div>
          ) : search.trim().length >= 2 ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Aucune machine accessible trouvée.
            </div>
          ) : (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Saisissez au moins deux caractères.
            </div>
          )}
        </div>

        <p className="text-xs text-[var(--do-muted)]">
          Seules les machines actives de votre périmètre GMAO sont proposées.
        </p>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="actaSecondary"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="actaPrimary"
            onClick={() => onAssociate(selectedMachine)}
            disabled={!selectedMachine || saving}
          >
            {saving ? "Association…" : "Associer"}
          </Button>
        </div>
      </div>
    </ActaModal>
  );
}
